import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ManualClGraphQlTransport } from "@/domains/thick-v1/manual-cl-graphql-transport"
import type { ManualClIndexerStatus } from "@/domains/validators"

export type IndexerFreshnessLevel = "fresh" | "warning" | "stale"

/**
 * Base L2 blocks land roughly every two seconds. Used to translate the
 * block-delta lag into an approximate "X behind" string for the UI.
 */
const BASE_SECONDS_PER_BLOCK = 2

/**
 * Lag thresholds, expressed in blocks so they scale with the indexer's
 * own progression rather than wall-clock. On Base these map to:
 *   - warning ≥ 30 blocks  ≈ 60s
 *   - stale   ≥ 300 blocks ≈ 10 min
 */
const AMBER_LAG_BLOCKS = 30
const RED_LAG_BLOCKS = 300

/**
 * Observable indexer-freshness store. Polls Envio's `chain_metadata`
 * table via {@link ManualClGraphQlTransport.loadIndexerStatus} and
 * derives a three-state `level` (fresh / warning / stale) from the
 * delta between the chain tip and the indexer's committed progress
 * block (`progressBlock` — i.e. `latest_processed_block`).
 *
 * We deliberately use the progress block rather than the buffered /
 * fetched block: the buffer reflects data pulled from HyperSync but
 * not necessarily committed to the DB, so a UI signal based on it
 * would claim the indexer is fresh before the user can actually read
 * the data. Using `progressBlock` keeps the banner honest.
 *
 * Freshness is computed purely from two in-chain block numbers, so
 * this store is immune to client-side wall-clock skew that previously
 * caused false "behind head" warnings on idle chains.
 */
export class IndexerStatusStore {
  status: ManualClIndexerStatus | undefined = undefined
  isLoading: boolean = false
  private pollIntervalMs: number
  private pollTimer: ReturnType<typeof setInterval> | undefined = undefined

  constructor(
    private readonly transport: ManualClGraphQlTransport,
    private readonly chainId: number = 8453,
    pollIntervalMs: number = 10_000,
  ) {
    this.pollIntervalMs = pollIntervalMs
    makeObservable(this, {
      status: observable,
      isLoading: observable,
      setStatus: action,
      lagBlocks: computed,
      lagSeconds: computed,
      level: computed,
      isHidden: computed,
    })
  }

  start(): void {
    if (this.pollTimer) return
    void this.refresh()
    this.pollTimer = setInterval(() => {
      void this.refresh()
    }, this.pollIntervalMs)
  }

  stop(): void {
    if (!this.pollTimer) return
    clearInterval(this.pollTimer)
    this.pollTimer = undefined
  }

  setStatus(status: ManualClIndexerStatus | undefined): void {
    this.status = status
  }

  /**
   * Blocks between the indexer's view of the chain tip (`headBlock`)
   * and the last block it has committed to the DB (`progressBlock`).
   * Clamped to zero — `progressBlock` can momentarily catch up to /
   * exceed `headBlock` in the tick between Envio advancing both.
   */
  get lagBlocks(): number {
    if (!this.status) return 0
    const lag = this.status.headBlock - this.status.progressBlock
    return lag > 0 ? lag : 0
  }

  /**
   * Block lag converted to an approximate seconds value using Base's
   * ~2 s block time. Purely for the user-facing "X behind" string —
   * thresholds still live in {@link lagBlocks}.
   */
  get lagSeconds(): number {
    return this.lagBlocks * BASE_SECONDS_PER_BLOCK
  }

  get level(): IndexerFreshnessLevel {
    const lag = this.lagBlocks
    if (lag >= RED_LAG_BLOCKS) return "stale"
    if (lag >= AMBER_LAG_BLOCKS) return "warning"
    return "fresh"
  }

  /**
   * Whether the top-of-page banner should hide itself entirely. True
   * when we have no status yet (endpoint unreachable / chain_metadata
   * missing) OR when we are within the fresh window — banners only
   * appear when they have something to say.
   */
  get isHidden(): boolean {
    if (!this.status) return true
    return this.level === "fresh"
  }

  private async refresh(): Promise<void> {
    if (this.isLoading) return
    runInAction(() => {
      this.isLoading = true
    })
    try {
      const next = await this.transport.loadIndexerStatus(this.chainId)
      runInAction(() => {
        this.status = next ?? undefined
        this.isLoading = false
      })
    } catch {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }
}
